import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

export const PORTAL_CANVAS_WIDTH = 1320
export const PORTAL_CANVAS_HEIGHT = 810

export function fitPortalScale(
  availableWidth: number,
  availableHeight: number,
  designWidth = PORTAL_CANVAS_WIDTH,
  designHeight = PORTAL_CANVAS_HEIGHT,
) {
  if (
    availableWidth <= 0 ||
    availableHeight <= 0 ||
    designWidth <= 0 ||
    designHeight <= 0
  ) return 1

  return Math.min(1, availableWidth / designWidth, availableHeight / designHeight)
}

type FittedPortalCanvasProps = {
  children: ReactNode
  fitMode?: 'viewport' | 'width'
  viewportReserve?: number
  maximumProductHeight?: number
}

/**
 * Keeps the desktop portal, float notes, and depth layers in one authored
 * coordinate system. Narrow phones use the portal's purpose-built mobile
 * composition instead of shrinking the desktop UI into unreadable pixels.
 */
export function FittedPortalCanvas({
  children,
  fitMode = 'viewport',
  viewportReserve = 220,
  maximumProductHeight = 650,
}: FittedPortalCanvasProps) {
  const shellRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 900px)').matches,
  )

  useLayoutEffect(() => {
    const shell = shellRef.current
    if (!shell) return

    const mobileQuery = window.matchMedia('(max-width: 900px)')
    const measure = () => {
      const mobile = mobileQuery.matches
      setIsMobile(mobile)
      if (mobile) {
        setScale(1)
        return
      }

      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      const availableWidth = shell.clientWidth || PORTAL_CANVAS_WIDTH
      const next = fitMode === 'width'
        ? Math.min(1, availableWidth / PORTAL_CANVAS_WIDTH)
        : fitPortalScale(
          availableWidth,
          Math.min(
            maximumProductHeight,
            Math.max(420, viewportHeight - viewportReserve),
          ),
        )
      setScale((current) => Math.abs(current - next) > 0.002 ? next : current)
    }

    measure()
    const observer = 'ResizeObserver' in window ? new ResizeObserver(measure) : null
    observer?.observe(shell)
    mobileQuery.addEventListener('change', measure)
    window.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('resize', measure)

    return () => {
      observer?.disconnect()
      mobileQuery.removeEventListener('change', measure)
      window.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('resize', measure)
    }
  }, [fitMode, maximumProductHeight, viewportReserve])

  if (isMobile) {
    return (
      <div
        className="fitted-portal-mobile-shell"
        data-canvas-mode="mobile"
        data-testid="fitted-portal-shell"
        ref={shellRef}
      >
        {children}
      </div>
    )
  }

  return (
    <div
      className="fitted-portal-shell"
      data-canvas-mode="fitted"
      data-fit-mode={fitMode}
      data-canvas-scale={scale.toFixed(3)}
      data-design-size={`${PORTAL_CANVAS_WIDTH}x${PORTAL_CANVAS_HEIGHT}`}
      data-fit-density={scale < 0.72 ? 'compact' : 'full'}
      data-testid="fitted-portal-shell"
      ref={shellRef}
      style={{ height: PORTAL_CANVAS_HEIGHT * scale }}
    >
      <div
        className="fitted-portal-canvas"
        style={{
          height: PORTAL_CANVAS_HEIGHT,
          marginLeft: PORTAL_CANVAS_WIDTH / -2,
          transform: `scale(${scale})`,
          width: PORTAL_CANVAS_WIDTH,
        }}
      >
        {children}
      </div>
    </div>
  )
}
